import { useEffect, useId, useState } from 'react';
import IconChevronDown from '~/vendor/cxy/icons/icons/ChevronDown';
import IconChevronDownLight from '~/vendor/cxy/icons/icons/ChevronDownLight';

import styles from './styles/Accordion.module.scss';

/**
 * Schedule `callback` two animation frames out, so a style change made now is painted before it runs.
 *
 * One frame is not enough: React commits the `hidden` removal and the callback would land in the same
 * frame, so the panel goes from `display: none` straight to its open style with no intermediate paint
 * and the transition never starts. Falls through to a plain call where `requestAnimationFrame` is
 * missing (SSR, a non-visual jsdom), where there is nothing to animate anyway.
 */
const afterNextPaint = (callback: () => void) => {
  if (typeof requestAnimationFrame !== 'function') {
    callback();
    return () => {};
  }

  let inner = 0;
  const outer = requestAnimationFrame(() => {
    inner = requestAnimationFrame(callback);
  });

  return () => {
    cancelAnimationFrame(outer);
    cancelAnimationFrame(inner);
  };
};

const useToggle = ({
  animationDurationMs = 0,
  indicatorSize = 'xsm',
  isOpen,
  isPanelLandmark = true,
  onChange,
  stroke = 'bold',
}: {
  /**
   * Length of the open/close transition, in milliseconds. `0` (the default) keeps the panel
   * switching instantly on the `hidden` attribute.
   *
   * Above zero the panel stays unhidden for this long while closing, carries `data-animated` and then
   * `data-enter` once it has entered, and publishes `--cxy-accordion-duration`. The fade itself is in
   * this component's stylesheet; a caller only overrides it to transition something other than opacity.
   */
  animationDurationMs?: number;
  indicatorSize?: 'xxsm' | 'xsm' | 'sm' | 'md';
  isOpen?: boolean;
  /**
   * Whether the panel is a `region` landmark. Pass `false` where many of these render together: the
   * ARIA practices guide says to avoid the role past roughly six panels, and beyond that a page of
   * same-labelled disclosures fills the landmark list with entries a reader cannot tell apart.
   */
  isPanelLandmark?: boolean;
  onChange?: (value: boolean) => void;
  stroke?: 'light' | 'bold';
}) => {
  const [isExpanded, setIsExpanded] = useState(isOpen);
  const isAnimated = animationDurationMs > 0;

  // Split from `isExpanded` only while animating: on close the panel has to stay unhidden until the
  // transition has run, and on open it has to be unhidden a paint BEFORE `hasEntered` flips.
  const [isUnhidden, setIsUnhidden] = useState(isOpen);
  // An item that renders open has already entered: starting it at `false` plays the enter transition
  // on first paint, so a panel the caller asked to be open fades in on every page load.
  const [hasEntered, setHasEntered] = useState(Boolean(isOpen));

  const headingId = useId();
  const sectionId = useId();

  const toggle = () => {
    onChange?.(!isExpanded);
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (isOpen !== isExpanded) {
      setIsExpanded(isOpen);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isAnimated) return;

    if (isExpanded) {
      setIsUnhidden(true);
      return afterNextPaint(() => setHasEntered(true));
    }

    setHasEntered(false);
    const timer = setTimeout(() => setIsUnhidden(false), animationDurationMs);
    return () => clearTimeout(timer);
  }, [isExpanded, isAnimated, animationDurationMs]);

  const headingProps = {
    'aria-controls': sectionId,
    // The watermark travels with the hook, not just with `Accordion.Item`. A caller that drives the
    // disclosure headlessly renders cxy's behaviour on markup of its own — the footer nav does — and
    // without this it is indistinguishable in the DOM from a hand-rolled toggle, which is the one
    // question `data-cxy-name` exists to answer.
    'data-cxy-name': 'accordion',
    'aria-expanded': isExpanded,
    'data-state': isExpanded ? 'open' : 'closed',
    id: headingId,
    onClick: toggle,
  };

  const sectionProps = {
    'aria-labelledby': headingId,
    className: styles.contentElement,
    hidden: isAnimated ? !isUnhidden : !isExpanded,
    id: sectionId,
    ...(isPanelLandmark ? { role: 'region' } : {}),
    ...(isAnimated ? { 'data-animated': '' } : {}),
    ...(isAnimated && hasEntered ? { 'data-enter': '' } : {}),
  };

  return {
    headingProps,
    sectionProps,
    triggerClass: styles.triggerElement,
    Indicator: () =>
      stroke === 'bold' ? (
        <IconChevronDown className={styles.indicator} size={indicatorSize} />
      ) : (
        <IconChevronDownLight className={styles.indicator} size={indicatorSize} />
      ),
  };
};

export { useToggle };
